import { Box, CircularProgress, useTheme } from "@mui/material";
import React, { useState, FC } from "react";

interface IframeContentProps {
  signedUrl: URL | undefined;
}

export const IframeContent: FC<IframeContentProps> = ({ signedUrl }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(true);

  React.useEffect(() => {
    // Data changed, prepare for a reload
    setLoading(true);
  }, [signedUrl]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      {loading && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
          }}
        >
          <CircularProgress size="1.75rem" />
        </Box>
      )}

      {signedUrl && (
        <iframe
          // Sandbox restricts what embedded content can do.
          // See https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/iframe#sandbox
          //
          // allow-scripts: Customer HTML files (e.g. analytics reports) contain JS
          //   for charts, maps, and interactive tables.
          // allow-same-origin: Without this, the iframe gets an opaque origin which
          //   strips the Referer header from sub-resource requests. OpenStreetMap tile
          //   servers (used by Plotly maps) require a Referer and return 403 without one.
          //   This is safe because the iframe's origin (S3) differs from the app's origin,
          //   so it cannot access the app's cookies, storage, or DOM.
          // allow-popups: Customer reports contain target="_blank" links (e.g. links back
          //   to the app). Without this, clicking those links silently fails.
          // allow-popups-to-escape-sandbox: Opened tabs (e.g. links back to Roboto URLs)
          //   should not inherit the sandbox restrictions, which would break the app.
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          src={signedUrl.toString()}
          width="100%"
          style={{
            border: 0,
            opacity: loading ? 0 : 1,
            height: loading ? 0 : "100%",
            transition: "opacity 0.5s",
          }}
          loading="lazy"
          onLoad={() => setLoading(false)}
        />
      )}
    </Box>
  );
};
